// apps/web/src/lib/passwordReset/flow.ts
// Pure decision helpers for the self-service password-reset flow (M9), the mirror
// of lib/activation/flow.ts. Extracted from the two client islands (RequestForm,
// ResetForm) so the response mapping + submit gating are unit-testable without a
// DOM. All branching logic lives here; the islands wire it to React state, the
// BFF and the router.

import { meetsLocalPolicy, PASSWORD_MIN_ZXCVBN } from '@/lib/passwordStrength';

export const RATE_LIMITED_MESSAGE = 'Trop de tentatives. Réessayez dans un instant.';
export const WEAK_MESSAGE =
  'Mot de passe trop faible ou contenant vos informations personnelles. Choisissez-en un autre.';
export const REUSED_MESSAGE =
  'Ce mot de passe a déjà été utilisé récemment. Choisissez-en un autre.';

// POST /api/auth/password-reset/request outcome. The endpoint is anti-enumeration:
// a well-formed request always returns 200 { ok: true }, so a 2xx never confirms
// that an account exists — the UI shows the same generic confirmation either way.
//   2xx  -> generic confirmation screen
//   429  -> transient rate-limit toast (stay on the form)
//   else -> generic error (malformed body 400 is unreachable: the client gates
//           on a non-empty email; 5xx / network handled by the caller)
export type RequestOutcome =
  | { kind: 'sent' }
  | { kind: 'rateLimited'; message: string }
  | { kind: 'error' };

export function interpretRequest(status: number): RequestOutcome {
  if (status >= 200 && status < 300) return { kind: 'sent' };
  if (status === 429) return { kind: 'rateLimited', message: RATE_LIMITED_MESSAGE };
  return { kind: 'error' };
}

// POST /api/auth/password-reset/complete outcome mapping (status + generic error
// code). Unlike activation, success does NOT auto-login — the island redirects to
// the login screen.
//   2xx                          -> success (redirect to /login)
//   400 token_invalide           -> invalid screen (token gone/expired; ask a new link)
//   400 mot_de_passe_invalide    -> inline weak-password error (token survives)
//   400 mot_de_passe_deja_utilise-> inline reused-password error (token survives)
//   429                          -> transient rate-limit toast (stay on form)
//   else (500/unexpected)        -> generic error screen
export type CompleteOutcome =
  | { kind: 'success' }
  | { kind: 'invalid' }
  | { kind: 'weak'; message: string }
  | { kind: 'reused'; message: string }
  | { kind: 'rateLimited'; message: string }
  | { kind: 'error' };

export function interpretComplete(
  status: number,
  body: { error?: string },
): CompleteOutcome {
  if (status >= 200 && status < 300) return { kind: 'success' };
  if (status === 429) return { kind: 'rateLimited', message: RATE_LIMITED_MESSAGE };
  if (status === 400 && body.error === 'token_invalide') return { kind: 'invalid' };
  if (status === 400 && body.error === 'mot_de_passe_invalide') {
    return { kind: 'weak', message: WEAK_MESSAGE };
  }
  if (status === 400 && body.error === 'mot_de_passe_deja_utilise') {
    return { kind: 'reused', message: REUSED_MESSAGE };
  }
  return { kind: 'error' };
}

// Submit gate for the complete form: same PARTIAL client mirror as activation
// (length + classes + zxcvbn score) plus the confirmation match. The server stays
// authoritative (it additionally bans identity-derived and recently-used
// passwords), so passing this gate is necessary, not sufficient.
export function canSubmitReset(
  password: string,
  confirm: string,
  score: number,
): boolean {
  return (
    meetsLocalPolicy(password) &&
    score >= PASSWORD_MIN_ZXCVBN &&
    confirm.length > 0 &&
    password === confirm
  );
}
